import { z } from 'zod';
import type { CIDRRule, InterfaceRule } from '#dn-types/config-override.ts';
import type { ConfigOverride } from '#dn-types/host.ts';
import {
  DEFAULT_NETMASK_REQUIRED_v4_LOCAL_ERR,
  DEFAULT_NETMASK_REQUIRED_v4_REMOTE_ERR,
  DEFAULT_NETMASK_REQUIRED_v6_LOCAL_ERR,
  DEFAULT_NETMASK_REQUIRED_v6_REMOTE_ERR,
  filterInterface,
  filterIpV4,
  filterIpV6,
  MIXED_LOCAL_INTERFACE_ERR,
  validAllowDeny,
} from '#schemas/config-override/helpers/allow-list.ts';
import { overrideSchemas, type TagFormOverride } from './config-forms/form-data.ts';

// These refinements ensure that a default exists if there is a mix of allow and deny rules
const remoteAllowV4Refinement = (overrides: TagFormOverride[]) => {
  const rules = getRemoteRules(overrides);
  if (!rules.length) return true;
  return validAllowDeny(filterIpV4(rules));
};
const remoteAllowV6Refinement = (overrides: TagFormOverride[]) => {
  const rules = getRemoteRules(overrides);
  if (!rules.length) return true;
  return validAllowDeny(filterIpV6(rules));
};
const localAllowV4Refinement = (overrides: TagFormOverride[]) => {
  const rules = getLocalCIDRRules(overrides);
  if (!rules.length) return true;
  return validAllowDeny(filterIpV4(rules));
};
const localAllowV6Refinement = (overrides: TagFormOverride[]) => {
  const rules = getLocalCIDRRules(overrides);
  if (!rules.length) return true;
  return validAllowDeny(filterIpV6(rules));
};
const localAllowInterfaceRefinement = (overrides: TagFormOverride[]) => {
  const rules = getLocalInterfaceRules(overrides);
  if (!rules.length) return true;
  return validAllowDeny(filterInterface(rules));
};

/**
 * Form-side schema for a list of config overrides with allow lists split one rule per row (see
 * {@link splitOverrides}). Shared by the tag and network forms, which both edit a full
 * override set via the modal-based Add/Edit config flow.
 */
export const configOverridesFieldSchema = z
  .array(overrideSchemas)
  .refine(remoteAllowV4Refinement, { message: DEFAULT_NETMASK_REQUIRED_v4_REMOTE_ERR })
  .refine(remoteAllowV6Refinement, { message: DEFAULT_NETMASK_REQUIRED_v6_REMOTE_ERR })
  .refine(localAllowV4Refinement, { message: DEFAULT_NETMASK_REQUIRED_v4_LOCAL_ERR })
  .refine(localAllowV6Refinement, { message: DEFAULT_NETMASK_REQUIRED_v6_LOCAL_ERR })
  .refine(localAllowInterfaceRefinement, { message: MIXED_LOCAL_INTERFACE_ERR });

/**
 * Helper function to get a list of remote_allow_list cidr rules ({@link CIDRRule}) from a list of config overrides
 */
function getRemoteRules(overrides: ConfigOverride[]) {
  return overrides.flatMap((o) =>
    o.key === 'lighthouse.remote_allow_list' ? Object.entries(o.value).map(([cidr, allow]) => ({ cidr, allow })) : []
  );
}

/**
 * Helper function to get a list of local_allow_list cidr rules ({@link CIDRRule}) from a list of config overrides
 */
function getLocalCIDRRules(overrides: ConfigOverride[]): CIDRRule[] {
  return overrides.flatMap((o) => {
    if (o.key === 'lighthouse.local_allow_list') {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- not using interfaces, just cidrs
      const { interfaces, ...cidrs } = o.value;
      return Object.entries(cidrs).map(([cidr, allow]) => ({ cidr, allow: Boolean(allow) })); // already boolean, but TS doesn't know that
    }
    return [];
  });
}

/**
 * Helper function to get a list of local_allow_list interface rules ({@link InterfaceRule}) from a list of config overrides
 */
function getLocalInterfaceRules(overrides: ConfigOverride[]): InterfaceRule[] {
  return overrides.flatMap((o) => {
    if (o.key === 'lighthouse.local_allow_list') {
      const { interfaces } = o.value;
      return Object.entries(interfaces).map(([iface, allow]) => ({ interface: iface, allow }));
    }
    return [];
  });
}
